/**
 * championKitBoot — Register all champion ability dispatchers via OCP (AB-09).
 * abilityDispatcher seed Map stays empty; kits live here.
 */
import { registerChampionDispatcher } from './abilityDispatcher.js';
import {
  castAsheQ, castAsheW, castAsheE, castAsheR
} from '../domain/champions/ashe.js';
import {
  castGarenQ, castGarenW, castGarenE, castGarenR
} from '../domain/champions/garen.js';
import {
  castRyzeQ, castRyzeW, castRyzeE, castRyzeR
} from '../domain/champions/ryze.js';
import { castAnnieQ, castAnnieW, castAnnieE, castAnnieR } from '../domain/champions/annie.js';
import { castSivirQ, castSivirW, castSivirE, castSivirR } from '../domain/champions/sivir.js';
import {
  castDariusQ, castDariusW, castDariusE, castDariusR
} from '../domain/champions/darius.js';
import {
  castSettQ, castSettW, castSettE, castSettR
} from '../domain/champions/sett.js';
import {
  castJaxQ, castJaxW, castJaxE, castJaxR
} from '../domain/champions/jax.js';
import {
  castLuxQ, castLuxW, castLuxE, castLuxR
} from '../domain/champions/lux.js';
import {
  castBrandQ, castBrandW, castBrandE, castBrandR
} from '../domain/champions/brand.js';
import {
  castVeigarQ, castVeigarW, castVeigarE, castVeigarR
} from '../domain/champions/veigar.js';
import {
  castSorakaQ, castSorakaW, castSorakaE, castSorakaR
} from '../domain/champions/soraka.js';
import {
  castLuluQ, castLuluW, castLuluE, castLuluR
} from '../domain/champions/lulu.js';
import {
  castNamiQ, castNamiW, castNamiE, castNamiR
} from '../domain/champions/nami.js';
import './championKitBootAdc.js';
import './championKitBootAssassin.js';
import './championKitBootTank.js';
import './championKitBootSkirmisher.js';

function hasMana(player, cost) {
  return player.mp >= cost;
}

registerChampionDispatcher('ashe', {
  Q(player) {
    if (!castAsheQ(player)) return null;
    return { type: 'ashe_q', projectiles: [], cooldown: 5.0 };
  },
  W(player, worldPos, _projectiles, gameTime) {
    if (!hasMana(player, 50)) return null;
    player.mp -= 50;
    const bucket = [];
    castAsheW(player, worldPos.x, worldPos.y, bucket, gameTime);
    return { type: 'ashe_w', projectiles: bucket, cooldown: 8.0 };
  },
  E(player, worldPos, _projectiles, gameTime) {
    const bucket = [];
    if (!castAsheE(player, worldPos.x, worldPos.y, bucket, gameTime)) return null;
    return { type: 'ashe_e', projectiles: bucket, cooldown: 5.0 };
  },
  R(player, worldPos, _projectiles, gameTime) {
    if (!hasMana(player, 100)) return null;
    player.mp -= 100;
    const bucket = [];
    castAsheR(player, worldPos.x, worldPos.y, bucket, gameTime);
    return { type: 'ashe_r', projectiles: bucket, cooldown: 80.0 };
  }
});

registerChampionDispatcher('garen', {
  Q(player) {
    castGarenQ(player);
    return { type: 'garen_q', projectiles: [], cooldown: 8.0 };
  },
  W(player) {
    castGarenW(player);
    return { type: 'garen_w', projectiles: [], cooldown: 24.0 };
  },
  E(player, _worldPos, _projectiles, _gameTime, entities) {
    castGarenE(player, entities);
    return { type: 'garen_e', projectiles: [], cooldown: 9.0 };
  },
  R(player, _worldPos, _projectiles, _gameTime, _entities, selectedTarget) {
    if (!selectedTarget) return null;
    castGarenR(player, selectedTarget);
    return { type: 'garen_r', projectiles: [], cooldown: 100.0 };
  }
});

registerChampionDispatcher('ryze', {
  Q(player, worldPos, _projectiles, gameTime) {
    const bucket = [];
    if (!castRyzeQ(player, worldPos.x, worldPos.y, bucket, gameTime)) return null;
    return { type: 'ryze_q', projectiles: bucket, cooldown: 5.0 };
  },
  W(player, _worldPos, _projectiles, _gameTime, _entities, selectedTarget) {
    if (!selectedTarget) return null;
    if (!castRyzeW(player, selectedTarget)) return null;
    return { type: 'ryze_w', projectiles: [], cooldown: 11.0 };
  },
  E(player, _worldPos, _projectiles, _gameTime, entities, selectedTarget) {
    if (!selectedTarget) return null;
    if (!castRyzeE(player, selectedTarget, entities)) return null;
    return { type: 'ryze_e', projectiles: [], cooldown: 12.0 };
  },
  R(player, worldPos) {
    if (!castRyzeR(player, worldPos.x, worldPos.y)) return null;
    return { type: 'ryze_r', projectiles: [], cooldown: 80.0 };
  }
});

registerChampionDispatcher('annie', {
  Q(player, _worldPos, _projectiles, _gameTime, _entities, selectedTarget) {
    if (!castAnnieQ(player, selectedTarget)) return null;
    return { type: 'annie_q', projectiles: [], cooldown: 4.0 };
  },
  W(player, worldPos, _projectiles, gameTime) {
    const bucket = [];
    if (!castAnnieW(player, worldPos.x, worldPos.y, bucket, gameTime)) return null;
    return { type: 'annie_w', projectiles: bucket, cooldown: 8.0 };
  },
  E(player) {
    if (!castAnnieE(player)) return null;
    return { type: 'annie_e', projectiles: [], cooldown: 10.0 };
  },
  R(player, worldPos, _projectiles, _gameTime, entities) {
    if (!castAnnieR(player, worldPos.x, worldPos.y, entities)) return null;
    return { type: 'annie_r', projectiles: [], cooldown: 100.0 };
  }
});

registerChampionDispatcher('sivir', {
  Q(player, worldPos, _projectiles, gameTime) {
    const bucket = [];
    if (!castSivirQ(player, worldPos.x, worldPos.y, bucket, gameTime)) return null;
    return { type: 'sivir_q', projectiles: bucket, cooldown: 9.0 };
  },
  W(player) {
    if (!castSivirW(player)) return null;
    return { type: 'sivir_w', projectiles: [], cooldown: 9.0 };
  },
  E(player) {
    if (!castSivirE(player)) return null;
    return { type: 'sivir_e', projectiles: [], cooldown: 22.0 };
  },
  R(player) {
    if (!castSivirR(player)) return null;
    return { type: 'sivir_r', projectiles: [], cooldown: 100.0 };
  }
});

registerChampionDispatcher('darius', {
  Q(player, _worldPos, _projectiles, _gameTime, entities) {
    if (!castDariusQ(player, entities)) return null;
    return { type: 'darius_q', projectiles: [], cooldown: 9.0 };
  },
  W(player) {
    if (!castDariusW(player)) return null;
    return { type: 'darius_w', projectiles: [], cooldown: 7.0 };
  },
  E(player, worldPos, _projectiles, _gameTime, entities) {
    if (!castDariusE(player, worldPos.x, worldPos.y, entities)) return null;
    return { type: 'darius_e', projectiles: [], cooldown: 14.0 };
  },
  R(player, _worldPos, _projectiles, _gameTime, _entities, selectedTarget) {
    if (!selectedTarget) return null;
    if (!castDariusR(player, selectedTarget)) return null;
    return { type: 'darius_r', projectiles: [], cooldown: 100.0 };
  }
});

registerChampionDispatcher('sett', {
  Q(player) {
    if (!castSettQ(player)) return null;
    return { type: 'sett_q', projectiles: [], cooldown: 8.0 };
  },
  W(player, worldPos, _projectiles, _gameTime, entities) {
    if (!castSettW(player, worldPos.x, worldPos.y, entities)) return null;
    return { type: 'sett_w', projectiles: [], cooldown: 16.0 };
  },
  E(player, _worldPos, _projectiles, _gameTime, entities) {
    if (!castSettE(player, entities)) return null;
    return { type: 'sett_e', projectiles: [], cooldown: 12.0 };
  },
  R(player, _worldPos, _projectiles, _gameTime, _entities, selectedTarget) {
    if (!selectedTarget) return null;
    if (!castSettR(player, selectedTarget)) return null;
    return { type: 'sett_r', projectiles: [], cooldown: 100.0 };
  }
});

registerChampionDispatcher('jax', {
  Q(player, _worldPos, _projectiles, _gameTime, _entities, selectedTarget) {
    if (!selectedTarget) return null;
    if (!castJaxQ(player, selectedTarget)) return null;
    return { type: 'jax_q', projectiles: [], cooldown: 8.0 };
  },
  W(player) {
    if (!castJaxW(player)) return null;
    return { type: 'jax_w', projectiles: [], cooldown: 5.0 };
  },
  E(player, _worldPos, _projectiles, _gameTime, entities) {
    if (!castJaxE(player, entities)) return null;
    return { type: 'jax_e', projectiles: [], cooldown: 14.0 };
  },
  R(player) {
    if (!castJaxR(player)) return null;
    return { type: 'jax_r', projectiles: [], cooldown: 80.0 };
  }
});

registerChampionDispatcher('lux', {
  Q(player, worldPos, _projectiles, gameTime) {
    const bucket = [];
    if (!castLuxQ(player, worldPos.x, worldPos.y, bucket, gameTime)) return null;
    return { type: 'lux_q', projectiles: bucket, cooldown: 10.0 };
  },
  W(player, worldPos, _projectiles, gameTime, entities) {
    const bucket = [];
    if (!castLuxW(player, worldPos.x, worldPos.y, bucket, gameTime, entities)) return null;
    return { type: 'lux_w', projectiles: bucket, cooldown: 12.0 };
  },
  E(player, worldPos, _projectiles, _gameTime, entities) {
    if (!castLuxE(player, worldPos.x, worldPos.y, entities)) return null;
    return { type: 'lux_e', projectiles: [], cooldown: 10.0 };
  },
  R(player, worldPos, _projectiles, _gameTime, entities) {
    if (!castLuxR(player, worldPos.x, worldPos.y, entities)) return null;
    return { type: 'lux_r', projectiles: [], cooldown: 80.0 };
  }
});

registerChampionDispatcher('brand', {
  Q(player, worldPos, _projectiles, gameTime) {
    const bucket = [];
    if (!castBrandQ(player, worldPos.x, worldPos.y, bucket, gameTime)) return null;
    return { type: 'brand_q', projectiles: bucket, cooldown: 8.0 };
  },
  W(player, worldPos, _projectiles, _gameTime, entities) {
    if (!castBrandW(player, worldPos.x, worldPos.y, entities)) return null;
    return { type: 'brand_w', projectiles: [], cooldown: 10.0 };
  },
  E(player, _worldPos, _projectiles, _gameTime, entities, selectedTarget) {
    if (!selectedTarget) return null;
    if (!castBrandE(player, selectedTarget, entities)) return null;
    return { type: 'brand_e', projectiles: [], cooldown: 13.0 };
  },
  R(player, _worldPos, _projectiles, _gameTime, entities, selectedTarget) {
    if (!selectedTarget) return null;
    if (!castBrandR(player, selectedTarget, entities)) return null;
    return { type: 'brand_r', projectiles: [], cooldown: 100.0 };
  }
});

registerChampionDispatcher('veigar', {
  Q(player, worldPos, _projectiles, gameTime) {
    const bucket = [];
    if (!castVeigarQ(player, worldPos.x, worldPos.y, bucket, gameTime)) return null;
    return { type: 'veigar_q', projectiles: bucket, cooldown: 6.0 };
  },
  W(player, worldPos, _projectiles, _gameTime, entities) {
    if (!castVeigarW(player, worldPos.x, worldPos.y, entities)) return null;
    return { type: 'veigar_w', projectiles: [], cooldown: 10.0 };
  },
  E(player, worldPos) {
    if (!castVeigarE(player, worldPos.x, worldPos.y)) return null;
    return { type: 'veigar_e', projectiles: [], cooldown: 18.0 };
  },
  R(player, _worldPos, _projectiles, _gameTime, _entities, selectedTarget) {
    if (!selectedTarget) return null;
    if (!castVeigarR(player, selectedTarget)) return null;
    return { type: 'veigar_r', projectiles: [], cooldown: 100.0 };
  }
});

registerChampionDispatcher('soraka', {
  Q(player, worldPos, _projectiles, _gameTime, entities) {
    if (!castSorakaQ(player, worldPos.x, worldPos.y, entities)) return null;
    return { type: 'soraka_q', projectiles: [], cooldown: 8.0 };
  },
  W(player, _worldPos, _projectiles, _gameTime, entities, selectedTarget) {
    if (!castSorakaW(player, entities, selectedTarget)) return null;
    return { type: 'soraka_w', projectiles: [], cooldown: 6.0 };
  },
  E(player, worldPos, _projectiles, _gameTime, entities) {
    if (!castSorakaE(player, worldPos.x, worldPos.y, entities)) return null;
    return { type: 'soraka_e', projectiles: [], cooldown: 16.0 };
  },
  R(player, _worldPos, _projectiles, _gameTime, entities) {
    if (!castSorakaR(player, entities)) return null;
    return { type: 'soraka_r', projectiles: [], cooldown: 100.0 };
  }
});

registerChampionDispatcher('lulu', {
  Q(player, worldPos, _projectiles, gameTime) {
    const bucket = [];
    if (!castLuluQ(player, worldPos.x, worldPos.y, bucket, gameTime)) return null;
    return { type: 'lulu_q', projectiles: bucket, cooldown: 7.0 };
  },
  W(player, _worldPos, _projectiles, _gameTime, _entities, selectedTarget) {
    if (!castLuluW(player, selectedTarget || player)) return null;
    return { type: 'lulu_w', projectiles: [], cooldown: 18.0 };
  },
  E(player, _worldPos, _projectiles, _gameTime, _entities, selectedTarget) {
    if (!castLuluE(player, selectedTarget)) return null;
    return { type: 'lulu_e', projectiles: [], cooldown: 10.0 };
  },
  R(player, _worldPos, _projectiles, _gameTime, entities, selectedTarget) {
    if (!castLuluR(player, entities, selectedTarget)) return null;
    return { type: 'lulu_r', projectiles: [], cooldown: 100.0 };
  }
});

registerChampionDispatcher('nami', {
  Q(player, worldPos, _projectiles, _gameTime, entities) {
    if (!castNamiQ(player, worldPos.x, worldPos.y, entities)) return null;
    return { type: 'nami_q', projectiles: [], cooldown: 12.0 };
  },
  W(player, _worldPos, _projectiles, _gameTime, entities, selectedTarget) {
    if (!castNamiW(player, entities, selectedTarget)) return null;
    return { type: 'nami_w', projectiles: [], cooldown: 10.0 };
  },
  E(player, _worldPos, _projectiles, _gameTime, _entities, selectedTarget) {
    if (!castNamiE(player, selectedTarget || player)) return null;
    return { type: 'nami_e', projectiles: [], cooldown: 11.0 };
  },
  R(player, worldPos, _projectiles, gameTime) {
    const bucket = [];
    if (!castNamiR(player, worldPos.x, worldPos.y, bucket, gameTime)) return null;
    return { type: 'nami_r', projectiles: bucket, cooldown: 100.0 };
  }
});

export const CORE_CHAMPION_KEYS = ['ashe', 'garen', 'ryze'];
export const P3_21_CHAMPION_KEYS = ['annie', 'sivir'];
export const SPY30_FIGHTER_KEYS = ['darius', 'sett', 'jax'];
export const SPY110_MAGE_KEYS = ['lux', 'brand', 'veigar'];
export const SPY210_SUPPORT_KEYS = ['soraka', 'lulu', 'nami'];
export { SPY310_ADC_KEYS } from './championKitBootAdc.js';
export { SPY710_TANK_KEYS } from './championKitBootTank.js';
export { SPY810_SKIRMISHER_KEYS } from './championKitBootSkirmisher.js';